import React, { useEffect, useState } from 'react';
import { AddOutlined } from '@mui/icons-material';
import { FormControlLabel, Skeleton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { DataPresetResponse, DataPresetType, HeroModuleItemType } from '../../../../API';
import useConfirm from '../../../../hooks/General/useConfirm';
import { useLocales, useNotifications, useTheme } from '../../../../hooks';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { HomepageOptions } from '../../../../state/theme';
import { NewHeroPresetTypes } from '../../../../utils/uiModuleUtils';
import { HelpText } from '../../HelpText/HelpText';
import Button from '../../Button';
import Repeat from '../../Repeat';
import { HeroPresetItem } from './HeroPresetItem';
import { makeStyles } from 'tss-react/mui';
import { useData } from '../../../../data-layer';
import { useRecoilValue } from 'recoil';
import { heroPresetsTestIds } from '../../TestsIds';

export interface HeroPresetsProps {
  contentId: string;
}

export const useStyles = makeStyles()((theme) => ({
  newPresetButton: {
    minWidth: 180,
    margin: theme.spacing(4)
  },
  skeleton: {
    flexBasis: 1,
    flexGrow: 1,
    height: 120,
    margin: theme.spacing(2)
  },
  noPresets: {
    padding: theme.spacing(6, 4)
  },
  multipleArtworkAll: {
    padding: theme.spacing(2, 4),
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}));

const isMultipleArtworkOn = (preset: DataPresetResponse) => !!(preset.data as NewHeroPresetTypes)?.multipleArtwork;

export function HeroPresets({ contentId }: HeroPresetsProps): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const { confirm } = useConfirm();
  const { notifySuccess } = useNotifications();
  const { formControlColor } = useTheme();
  // Same permission as saving a preset in the preset form
  const { canSave } = usePermissionsGuard({ homepageOption: HomepageOptions.HPC });
  const [isSavingMultipleArtwork, setIsSavingMultipleArtwork] = useState(false);
  const {
    presets: {
      state: { withFormMetadata, withRecordBucket, withIsFetching },
      hook: { removePreset, new: newPreset, closeForm: closePresetForm, getBucket, updatePreset }
    }
  } = useData();
  const isLoading = useRecoilValue(withIsFetching);
  const formPreset = useRecoilValue(withFormMetadata);

  const presets = useRecoilValue(withRecordBucket(contentId));

  useEffect(() => {
    getBucket(contentId);
  }, [contentId]);

  const onNewPresetClick = () => {
    newPreset({
      type: DataPresetType.HERO_ITEM_VOD,
      data: {
        type: DataPresetType.HERO_ITEM_VOD,
        contentType: HeroModuleItemType.VOD,
        contentId
      }
    });
  };

  const onPresetDelete = async (index: number) => {
    const preset = presets?.[index];
    if (preset?.id) {
      const confirmed = await confirm({
        confirmColor: 'error',
        confirmText: t('general.confirm_delete'),
        body: t('presets.delete_preset_confirm'),
        'data-testid': heroPresetsTestIds.deleteConfirm
      });
      if (!confirmed) return;
      const deleted = await removePreset(preset.id);
      if (!deleted) return;
      if (preset.id === (formPreset.record as DataPresetResponse)?.id) closePresetForm();
    }
  };

  // Off while the preset form is open: saving the form would overwrite a change made here
  const isMultipleArtworkDisabled = !canSave || formPreset.isShowingForm || isSavingMultipleArtwork;

  /**
   * Saves the flag on each preset, one at a time. The API replaces the whole `data` on update, so each preset is
   * sent whole with only the flag changed. Returns how many saved; a failed save shows its own error.
   */
  const saveMultipleArtwork = async (
    targets: DataPresetResponse[],
    multipleArtwork: boolean,
    suppressSuccessMessage = false
  ): Promise<number> => {
    setIsSavingMultipleArtwork(true);
    let saved = 0;
    try {
      for (const preset of targets) {
        const response = await updatePreset(
          preset.id as string,
          { ...preset, data: { ...preset.data, multipleArtwork } as DataPresetResponse['data'] },
          suppressSuccessMessage
        );
        if (response) saved++;
      }
    } finally {
      setIsSavingMultipleArtwork(false);
    }
    return saved;
  };

  const onAllMultipleArtworkChange = async (multipleArtwork: boolean) => {
    if (!presets?.length) return;
    const confirmed = await confirm({
      confirmText: t('general.confirm'),
      body: t(`presets.multiple_artwork_confirm_${multipleArtwork ? 'on' : 'off'}`, { count: presets.length }),
      'data-testid': heroPresetsTestIds.multipleArtworkConfirm
    });
    if (!confirmed) return;
    // Presets already at the chosen value are left untouched
    const targets = presets.filter((preset) => isMultipleArtworkOn(preset) !== multipleArtwork);
    const saved = await saveMultipleArtwork(targets, multipleArtwork, true);
    if (saved) notifySuccess(t('presets.multiple_artwork_updated', { count: saved }));
  };

  const multipleArtworkOnCount = presets?.filter(isMultipleArtworkOn).length ?? 0;

  return (
    <div>
      {!isLoading && !!presets?.length && (
        <Stack direction="row" alignItems="center" gap={2} className={classes.multipleArtworkAll}>
          <FormControlLabel
            control={
              <Switch
                size="medium"
                color={formControlColor}
                // On only when every preset is on, so from a mixed state the switch turns them all on
                checked={multipleArtworkOnCount === presets.length}
                disabled={isMultipleArtworkDisabled}
                onChange={({ target }) => onAllMultipleArtworkChange(target.checked)}
                inputProps={
                  {
                    'data-testid': heroPresetsTestIds.multipleArtworkAllSwitch
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={
              <Tooltip title={<HelpText>{t('presets.multiple_artwork_all_helper')}</HelpText>} placement="top-start">
                <span>{t('presets.multiple_artwork_all')}</span>
              </Tooltip>
            }
          />
          <Typography variant="body2" color="textSecondary" data-testid={heroPresetsTestIds.multipleArtworkCount}>
            {t('presets.multiple_artwork_count', { on: multipleArtworkOnCount, total: presets.length })}
          </Typography>
        </Stack>
      )}
      {!isLoading &&
        !!presets?.length &&
        presets.map((preset, i) => (
          <HeroPresetItem
            key={i}
            presetId={preset.id as string}
            onRemove={() => onPresetDelete(i)}
            onMultipleArtworkChange={(record, multipleArtwork) => saveMultipleArtwork([record], multipleArtwork)}
            multipleArtworkDisabled={isMultipleArtworkDisabled}
            data-testid={heroPresetsTestIds.heroPresetItem}
          />
        ))}
      {!isLoading && !presets?.length && (
        <div className={classes.noPresets}>
          <Typography variant="body1" color="textSecondary">
            {t('presets.no_presets')}
          </Typography>
        </div>
      )}
      {isLoading && (
        <div data-testid={heroPresetsTestIds.loader}>
          <Repeat n={3}>
            <Skeleton className={classes.skeleton} animation="wave" />
          </Repeat>
        </div>
      )}
      <Button
        className={classes.newPresetButton}
        endIcon={<AddOutlined />}
        onClick={onNewPresetClick}
        data-testid={heroPresetsTestIds.newPresetButton}
        disabled={isLoading || formPreset.isShowingForm}
      >
        {t('presets.new_preset')}
      </Button>
    </div>
  );
}
